/**
 * @fileoverview Field mapping definitions for conflict resolution UI.
 *
 * This module maps 534EZ form fields to their artifact data sources (DD-214,
 * death certificates) and provides the display and write-back logic the conflict
 * UI needs. The semantic normalization of values is performed elsewhere; see the
 * "Normalization layers" section below.
 *
 * ## Field Definition Shape
 *
 * Each field definition is an object with the following structure:
 *
 * @typedef {Object} FieldDefinition
 * @property {string} id - Unique field identifier used as the canonical key
 * @property {string} label - Human-readable field label for display
 * @property {string} editPath - Form route path for editing this field
 * @property {Function} getFormValue - Extracts field value from formData
 * @property {Function} normalize - Produces a comparison key for conflict
 *   detection (see "Normalization layers" below)
 * @property {Function} formatValue - Formats canonical value for display in the UI
 * @property {Function} applyToForm - Writes canonical value back into formData structure
 * @property {Array<ArtifactDefinition>} artifacts - Array of artifact source definitions
 *
 * ## Artifact Definition Shape
 *
 * @typedef {Object} ArtifactDefinition
 * @property {string} artifactKey - Key into normalized artifact collection ('dd214', 'deathCertificates')
 * @property {string} docTypeLabel - Human-readable document type label for the conflict UI
 * @property {Function} getArtifactValue - Extracts value from normalized artifact entry
 * @property {Function} formatArtifactValue - Formats artifact value for display
 * @property {Function} setArtifactValue - Writes canonical value back into artifact entry
 *
 * ## Normalization layers
 *
 * Normalization occurs in two distinct places, which serve different purposes:
 *
 * 1. **Ingestion (normalize.js)**: `normalizeArtifacts()` converts raw CAVE
 *    payloads into canonical values using functions such as `normalizeSsn`,
 *    `normalizeBranchOfService`, and `normalizeArtifactDate` (for example,
 *    'ARMY USAR' becomes 'army', '123-45-6789' becomes '123456789', and
 *    '01/02/2020' becomes '2020-01-02'). The null-versus-empty-string contract
 *    is defined at this layer: those normalizers return `null` for unparseable
 *    input and `''` for a field that is present but blank.
 *
 * 2. **Comparison (the `normalize` in each field definition below)**: By the time
 *    values reach this module they are already canonical — artifact values via
 *    the ingestion layer, and form values via the form schema. Each field's
 *    `normalize` therefore only produces a stable equality key for conflict
 *    detection, which compares `normalize(artifactValue)` against
 *    `normalize(formValue)` in conflictDetection.js. Most fields use
 *    `val => val || ''`, coalescing nullish input to an empty string for a clean
 *    comparison; `veteranName` is the exception, flattening the name object into
 *    a comparable string.
 *
 * The field-level `normalize` functions do not re-derive canonical form (no SSN
 * stripping, date parsing, or branch mapping) and never return `null`; that
 * behavior belongs to normalize.js. All `normalize` functions must be pure (no
 * mutations, no I/O).
 */

import { formatFullName } from '../../utils/helpers';
import { formatIsoDate, maskSsn, sanitize } from './helpers';
import { normalizeSsn } from './normalize';
import { servicesOptions } from '../../utils/labels';

const normalizeName = str =>
  (str || '').toLowerCase().trim().replace(/\s+/g, ' ');

// Looks up the human-readable label for a 534 service branch value.
// Falls back to sanitize() for unrecognized values.
const formatBranchLabel = val =>
  servicesOptions.find(o => o.value === val)?.label ?? sanitize(val);

/**
 * Field definitions for veteran personal information section.
 * Maps form fields to artifact data sources with normalization and formatting logic.
 *
 * @type {Array<FieldDefinition>}
 */
export const VETERAN_INFO_FIELDS = [
  {
    id: 'veteranName',
    label: 'Veteran name',
    editPath: 'veteran',
    getFormValue: formData => formData.veteranFullName,
    normalize: val => normalizeName(formatFullName(val || {})),
    formatValue: val => formatFullName(val || {}),
    applyToForm: (formData, canonicalValue) => ({
      ...formData,
      veteranFullName: {
        ...(formData.veteranFullName || {}),
        ...canonicalValue,
      },
    }),
    artifacts: [
      {
        artifactKey: 'dd214',
        docTypeLabel: 'DD-214',
        getArtifactValue: entry =>
          entry.veteranName?.first && entry.veteranName?.last
            ? entry.veteranName
            : null,
        formatArtifactValue: val => formatFullName(val || {}),
        setArtifactValue: (entry, canonicalValue) => ({
          ...entry,
          veteranName: canonicalValue,
        }),
      },
      {
        artifactKey: 'deathCertificates',
        docTypeLabel: 'death certificate',
        getArtifactValue: entry =>
          entry.decendentFullName?.first && entry.decendentFullName?.last
            ? entry.decendentFullName
            : null,
        formatArtifactValue: val => formatFullName(val || {}),
        setArtifactValue: (entry, canonicalValue) => ({
          ...entry,
          decendentFullName: canonicalValue,
        }),
      },
    ],
  },
  {
    id: 'veteranSsn',
    label: 'Social Security number',
    editPath: 'veteran-identification',
    getFormValue: formData => formData.veteranSocialSecurityNumber?.ssn,
    // Values are already bare 9 digits here (form schema "^[0-9]{9}$" and
    // artifact ingestion via normalizeSsn); coalesce nullish to '' for a clean
    // equality comparison.
    normalize: val => val || '',
    formatValue: val => maskSsn(val),
    // canonicalValue is bare 9 digits; write it as-is to match the schema pattern.
    applyToForm: (formData, canonicalValue) => ({
      ...formData,
      veteranSocialSecurityNumber: {
        ...(formData.veteranSocialSecurityNumber || {}),
        ssn: canonicalValue || '',
      },
    }),
    artifacts: [
      {
        artifactKey: 'dd214',
        docTypeLabel: 'DD-214',
        getArtifactValue: entry => normalizeSsn(entry.veteranSsn) || null,
        formatArtifactValue: val =>
          maskSsn((val || '').replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3')),
        setArtifactValue: (entry, canonicalValue) => ({
          ...entry,
          veteranSsn: (canonicalValue || '').replace(/\D/g, ''),
        }),
      },
      {
        artifactKey: 'deathCertificates',
        docTypeLabel: 'death certificate',
        getArtifactValue: entry => normalizeSsn(entry.decendentSsn) || null,
        formatArtifactValue: val =>
          maskSsn((val || '').replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3')),
        setArtifactValue: (entry, canonicalValue) => ({
          ...entry,
          decendentSsn: (canonicalValue || '').replace(/\D/g, ''),
        }),
      },
    ],
  },
  {
    id: 'veteranDateOfBirth',
    label: 'Date of birth',
    editPath: 'veteran',
    getFormValue: formData => formData.veteranDateOfBirth,
    // Values arrive already in ISO YYYY-MM-DD (form schema and
    // normalizeArtifactDate at ingestion); coalesce nullish to '' for comparison.
    normalize: val => val || '',
    formatValue: val => formatIsoDate(val),
    applyToForm: (formData, canonicalValue) => ({
      ...formData,
      veteranDateOfBirth: canonicalValue,
    }),
    artifacts: [
      {
        artifactKey: 'dd214',
        docTypeLabel: 'DD-214',
        getArtifactValue: entry => entry.veteranDob || null,
        formatArtifactValue: val => formatIsoDate(val),
        setArtifactValue: (entry, canonicalValue) => ({
          ...entry,
          veteranDob: canonicalValue,
        }),
      },
    ],
  },
  {
    id: 'veteranDateOfDeath',
    label: 'Date of death',
    editPath: 'veteran-additional-information',
    getFormValue: formData => formData.veteranDateOfDeath,
    normalize: val => val || '',
    formatValue: val => formatIsoDate(val),
    applyToForm: (formData, canonicalValue) => ({
      ...formData,
      veteranDateOfDeath: canonicalValue,
    }),
    artifacts: [
      {
        artifactKey: 'deathCertificates',
        docTypeLabel: 'death certificate',
        getArtifactValue: entry => entry.decendentDateOfDeath || null,
        formatArtifactValue: val => formatIsoDate(val),
        setArtifactValue: (entry, canonicalValue) => ({
          ...entry,
          decendentDateOfDeath: canonicalValue,
        }),
      },
    ],
  },
];

/**
 * Field definitions for military service history section.
 * Maps form fields to artifact data sources with normalization and formatting logic.
 *
 * @type {Array<FieldDefinition>}
 */
export const MILITARY_HISTORY_FIELDS = [
  {
    id: 'serviceBranch',
    label: 'Branch of service',
    editPath: 'service-period',
    getFormValue: formData => formData.serviceBranch,
    // Both form and artifact are 534 values ('army', 'airForce', etc.) after
    // Values arrive as 534 enum values ('army', 'airForce', etc.): form via
    // schema, artifact via normalizeBranchOfService at ingestion. Coalesce
    // nullish to '' so exact match is sufficient.
    normalize: val => val || '',
    formatValue: val => formatBranchLabel(val),
    applyToForm: (formData, canonicalValue) => ({
      ...formData,
      serviceBranch: canonicalValue,
    }),
    artifacts: [
      {
        artifactKey: 'dd214',
        docTypeLabel: 'DD-214',
        // normalizeArtifacts mapped the CAVE string to a 534 value
        getArtifactValue: entry => entry.branchOfService || null,
        formatArtifactValue: val => formatBranchLabel(val),
        setArtifactValue: (entry, canonicalValue) => ({
          ...entry,
          branchOfService: canonicalValue,
        }),
      },
    ],
  },
  {
    id: 'activeServiceStart',
    label: 'Date entered active service',
    editPath: 'service-period',
    getFormValue: formData => formData.activeServiceDateRange?.from,
    normalize: val => val || '',
    formatValue: val => formatIsoDate(val),
    applyToForm: (formData, canonicalValue) => ({
      ...formData,
      activeServiceDateRange: {
        ...(formData.activeServiceDateRange || {}),
        from: canonicalValue,
      },
    }),
    artifacts: [
      {
        artifactKey: 'dd214',
        docTypeLabel: 'DD-214',
        getArtifactValue: entry => entry.dateEnteredActiveService || null,
        formatArtifactValue: val => formatIsoDate(val),
        setArtifactValue: (entry, canonicalValue) => ({
          ...entry,
          dateEnteredActiveService: canonicalValue,
        }),
      },
    ],
  },
  {
    id: 'activeServiceEnd',
    label: 'Date separated from service',
    editPath: 'service-period',
    getFormValue: formData => formData.activeServiceDateRange?.to,
    normalize: val => val || '',
    formatValue: val => formatIsoDate(val),
    applyToForm: (formData, canonicalValue) => ({
      ...formData,
      activeServiceDateRange: {
        ...(formData.activeServiceDateRange || {}),
        to: canonicalValue,
      },
    }),
    artifacts: [
      {
        artifactKey: 'dd214',
        docTypeLabel: 'DD-214',
        getArtifactValue: entry => entry.dateSeparatedFromService || null,
        formatArtifactValue: val => formatIsoDate(val),
        setArtifactValue: (entry, canonicalValue) => ({
          ...entry,
          dateSeparatedFromService: canonicalValue,
        }),
      },
    ],
  },
];
